import _ from 'lodash';
import { combineReducers } from 'redux';

import types from 'frontend/state/actionTypes';
import { setterReducer, toggleReducer } from 'frontend/state/utils';

import {
  simcVersions,
  iterationOptions,
  fightStyles,
  fightLengths,
  enemyCounts,
  enemyTypes,
  getPotions,
  getFoods,
  getFlasks,
  getAugments,
} from 'shared/simcOptions';

import expansionOptions from 'shared/expansionOptions';

const PRESETS = {
  optimalRaid: {
    potion: '',
    food: '',
    flask: '',
    augmentation: '',
    optimalRaid: true,
    bloodlust: true,
    arcaneIntellect: true,
    fortitude: true,
    battleShout: true,
    mysticTouch: true,
    chaosBrand: true,
    bleeding: true,
    skyfury: true,
    markOfTheWild: true,
    powerInfusion: false,
    huntersMark: true,
  },
  targetDummy: {
    potion: 'disabled',
    food: 'disabled',
    flask: 'disabled',
    augmentation: 'disabled',
    optimalRaid: false,
    bloodlust: false,
    arcaneIntellect: false,
    fortitude: false,
    battleShout: false,
    mysticTouch: false,
    chaosBrand: false,
    bleeding: false,
    skyfury: false,
    markOfTheWild: false,
    powerInfusion: false,
    huntersMark: false,
  },
};

const presetValues = field => _.mapValues(PRESETS, value => value[field]);

const reducers = {
  open: (state = false, action = {}) => {
    const { type } = action;
    switch (type) {
      case types.TOGGLE_ADVANCED_OPTIONS:
        return !state;
      default:
        return state;
    }
  },
  simcVersion: setterReducer({
    set: types.SELECT_SIMC_VERSION,
    initial: _.find(simcVersions, { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    allowed: _.map(simcVersions, 'value'),
  }),
  iterations: setterReducer({
    set: types.SELECT_ITERATIONS,
    initial: _.find(iterationOptions, { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    allowed: _.map(iterationOptions, 'value'),
  }),
  smartHighPrecision: toggleReducer({
    toggle: types.TOGGLE_SMART_HIGH_PRECISION,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
  }),
  smartAggressive: toggleReducer({
    toggle: types.TOGGLE_SMART_AGGRESSIVE,
    initial: false,
    reset: [types.RESET_ADVANCED_OPTIONS],
  }),
  fightStyle: setterReducer({
    set: types.SELECT_FIGHT_STYLE,
    initial: _.find(fightStyles, { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    allowed: _.map(fightStyles, 'value'),
  }),
  fightLength: setterReducer({
    set: types.SELECT_FIGHT_LENGTH,
    initial: _.find(fightLengths, { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    allowed: _.map(fightLengths, 'value'),
  }),
  enemyCount: setterReducer({
    set: types.SELECT_ENEMY_COUNT,
    initial: _.find(enemyCounts, { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    allowed: _.map(enemyCounts, 'value'),
  }),
  enemyType: setterReducer({
    set: types.SELECT_ENEMY_TYPE,
    initial: _.find(enemyTypes, { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    allowed: _.map(enemyTypes, 'value'),
  }),
  potion: setterReducer({
    set: types.SELECT_POTION,
    initial: _.find(getPotions(), { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('potion'),
    },
    allowed: _.map(getPotions(), 'value'),
  }),
  food: setterReducer({
    set: types.SELECT_FOOD,
    initial: _.find(getFoods(), { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('food'),
    },
    allowed: _.map(getFoods(), 'value'),
  }),
  flask: setterReducer({
    set: types.SELECT_FLASK,
    initial: _.find(getFlasks(), { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('flask'),
    },
    allowed: _.map(getFlasks(), 'value'),
  }),
  augmentation: setterReducer({
    set: types.SELECT_AUGMENTATION,
    initial: _.find(getAugments(), { default: true }).value,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('augmentation'),
    },
    allowed: _.map(getAugments(), 'value'),
  }),
  optimalRaid: toggleReducer({
    toggle: types.TOGGLE_OPTIMAL_RAID,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('optimalRaid'),
    },
  }),
  bloodlust: toggleReducer({
    toggle: types.TOGGLE_BLOODLUST,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('bloodlust'),
    },
  }),
  arcaneIntellect: toggleReducer({
    toggle: types.TOGGLE_ARCANE_INTELLECT,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('arcaneIntellect'),
    },
  }),
  fortitude: toggleReducer({
    toggle: types.TOGGLE_FORTITUDE,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('fortitude'),
    },
  }),
  battleShout: toggleReducer({
    toggle: types.TOGGLE_BATTLE_SHOUT,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('battleShout'),
    },
  }),
  mysticTouch: toggleReducer({
    toggle: types.TOGGLE_MYSTIC_TOUCH,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('mysticTouch'),
    },
  }),
  chaosBrand: toggleReducer({
    toggle: types.TOGGLE_CHAOS_BRAND,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('chaosBrand'),
    },
  }),
  bleeding: toggleReducer({
    toggle: types.TOGGLE_BLEEDING,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('bleeding'),
    },
  }),
  skyfury: toggleReducer({
    toggle: types.TOGGLE_SKYFURY,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('skyfury'),
    },
  }),
  markOfTheWild: toggleReducer({
    toggle: types.TOGGLE_MARK_OF_THE_WILD,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('markOfTheWild'),
    },
  }),
  powerInfusion: toggleReducer({
    toggle: types.TOGGLE_POWER_INFUSION,
    initial: false,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('powerInfusion'),
    },
  }),
  huntersMark: toggleReducer({
    toggle: types.TOGGLE_HUNTERS_MARK,
    initial: true,
    reset: [types.RESET_ADVANCED_OPTIONS],
    presets: {
      action: types.SET_OPTIONS_PRESET,
      values: presetValues('huntersMark'),
    },
  }),
  reportDetails: toggleReducer({
    toggle: types.TOGGLE_REPORT_DETAILS,
    initial: false,
    reset: [types.RESET_ADVANCED_OPTIONS],
  }),
  ptr: toggleReducer({
    toggle: types.TOGGLE_PTR,
    initial: false,
    reset: [types.RESET_ADVANCED_OPTIONS],
  }),
  showAllOptions: toggleReducer({
    toggle: types.TOGGLE_SHOW_ALL_OPTIONS,
    initial: false,
    // do NOT attach reset action to Show All Options, weird UI if used
  }),
};

export const expansionOptionConfig = _.map(expansionOptions, opt => {
  return {
    key: opt.key,
    actionType: types[opt.redux.actionType],
    actionFunc: opt.redux.actionFunc,
  };
});

const expansionOptionsSelectors = {};

// automatically make reducers and selectors for the options above
_.each(expansionOptions, option => {
  let reducer;
  const reduxConfig = _.find(expansionOptionConfig, { key: option.key });
  if (!reduxConfig) {
    throw new Error(`No redux config found for expansion option ${option.key} - check for typo/missing entries`);
  }

  let presets;
  if (option.presets) {
    presets = {
      action: types.SET_OPTIONS_PRESET,
      values: option.presets,
    };
  }

  switch (option.type) {
    case 'int': {
      reducer = setterReducer({
        set: reduxConfig.actionType,
        initial: option.default,
        reset: [types.RESET_ADVANCED_OPTIONS],
        allowed: _.map(option.values, 'value'),
        presets,
      });
      break;
    }
    case 'bool': {
      reducer = toggleReducer({
        toggle: reduxConfig.actionType,
        initial: option.default,
        reset: [types.RESET_ADVANCED_OPTIONS],
        presets,
      });
      break;
    }
    case 'string': {
      reducer = setterReducer({
        set: reduxConfig.actionType,
        initial: option.default,
        reset: [types.RESET_ADVANCED_OPTIONS],
        allowed: _.map(option.values, 'value'),
        presets,
      });
      break;
    }
    default:
      throw new Error(`Unhandled expansion option type. Key ${option.key}, type ${option.type}`);
  }

  reducers[option.key] = reducer;
  expansionOptionsSelectors[option.key] = state => state.simbot.simcOptions[option.key];
});

export default combineReducers(reducers);

export const selectors = {
  // is advanced controls toggle open or closed
  open: state => state.simbot.simcOptions.open,

  simcVersion: state => state.simbot.simcOptions.simcVersion,
  iterations: state => state.simbot.simcOptions.iterations,
  smartHighPrecision: state => state.simbot.simcOptions.smartHighPrecision,
  smartAggressive: state => state.simbot.simcOptions.smartAggressive,
  fightStyle: state => state.simbot.simcOptions.fightStyle,
  fightLength: state => state.simbot.simcOptions.fightLength,
  enemyCount: state => state.simbot.simcOptions.enemyCount,
  enemyType: state => state.simbot.simcOptions.enemyType,

  potion: state => state.simbot.simcOptions.potion,
  food: state => state.simbot.simcOptions.food,
  flask: state => state.simbot.simcOptions.flask,
  augmentation: state => state.simbot.simcOptions.augmentation,

  optimalRaid: state => state.simbot.simcOptions.optimalRaid,
  bloodlust: state => state.simbot.simcOptions.bloodlust,
  arcaneIntellect: state => state.simbot.simcOptions.arcaneIntellect,
  fortitude: state => state.simbot.simcOptions.fortitude,
  battleShout: state => state.simbot.simcOptions.battleShout,
  mysticTouch: state => state.simbot.simcOptions.mysticTouch,
  chaosBrand: state => state.simbot.simcOptions.chaosBrand,
  bleeding: state => state.simbot.simcOptions.bleeding,
  skyfury: state => state.simbot.simcOptions.skyfury,
  markOfTheWild: state => state.simbot.simcOptions.markOfTheWild,
  powerInfusion: state => state.simbot.simcOptions.powerInfusion,
  huntersMark: state => state.simbot.simcOptions.huntersMark,

  ...expansionOptionsSelectors,

  reportDetails: state => state.simbot.simcOptions.reportDetails,
  ptr: state => state.simbot.simcOptions.ptr,

  showAllOptions: state => state.simbot.simcOptions.showAllOptions,
};
